import { useState, useEffect } from "react";
import {
  FaEdit,
  FaCheck,
  FaExternalLinkAlt,
  FaImage,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import { useApi } from "../context/useApi";


const ProductDetails = ({ product, goBack }) => {
  const [editing, setEditing] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "" });
  const { apiFetch } = useApi();
  const [form, setForm] = useState({
    product_name: "",
    brand_name: "",
    primary_category: "",
    secondary_category: "",
    product_type: "",
    product_image_url: "",
    product_link: "",
    description: "",
    tags: [],
    remarks: "",
    variants: [],
  });

  useEffect(() => {
    if (!product) return;
    setForm({
      product_name: product.product_name || "",
      brand_name: product.brand_name || "",
      primary_category: product.primary_category || "",
      secondary_category: product.secondary_category || "",
      product_type: product.product_type || "",
      product_image_url: product.product_image_url || "",
      product_link: product.product_link || "",
      description: product.description || "",
      tags: product.tags || [],
      remarks: product.remarks || "",
      variants: product.variants || [],
    });
  }, [product]);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };

const handleSave = async () => {
  setLoadingSave(true);
  try {
    // Adjust this endpoint if your backend uses a different path
    const res = await apiFetch(
      `https://admin-backend-fbuuh6fo3q-el.a.run.app/products/${product.product_id}`,
      {
        method: "PUT",
        body: JSON.stringify(form),
      }
    );
    if (!res) return;
    const data = await res.json();
    if (res.ok) {
      // reflect locally
      Object.assign(product, form);
      setEditing(false);
      showToast("Product details saved successfully", "success");
    } else {
      showToast(data.error || "Failed to save product details", "error");
    }
  } catch (err) {
    console.error("Product update error:", err);
    showToast("Failed to save product details", "error");
  } finally {
    setLoadingSave(false);
  }
};

  const handleCancel = () => {
    setForm({
      product_name: product.product_name || "",
      brand_name: product.brand_name || "",
      primary_category: product.primary_category || "",
      secondary_category: product.secondary_category || "",
      product_type: product.product_type || "",
      product_image_url: product.product_image_url || "",
      product_link: product.product_link || "",
      description: product.description || "",
      tags: product.tags || [],
      remarks: product.remarks || "",
      variants: product.variants || [],
    });
    setEditing(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Compact Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={goBack}
                className="text-gray-600 hover:text-gray-900 font-medium text-sm"
              >
                ← Products
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {form.product_name || "Untitled Product"}
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  ID: {product.product_id} •{" "}
                  {form.brand_name || "Unknown Brand"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {form.product_link && (
                <a
                  href={form.product_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 text-sm text-gray-700 hover:text-gray-900 flex items-center gap-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <FaExternalLinkAlt size={12} />
                  View Live
                </a>
              )}
              {!editing ? (
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2"
                >
                  <FaEdit size={14} />
                  Edit
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loadingSave}
                    className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                  >
                    <FaCheck size={14} />
                    {loadingSave ? "Saving..." : "Save Changes"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Image & Meta */}
          <div className="col-span-3 space-y-4">
            {/* Product Image Card */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Product Image
              </h3>
              {form.product_image_url ? (
                <img
                  src={form.product_image_url}
                  alt={form.product_name}
                  className="w-full rounded-lg border border-gray-200"
                />
              ) : (
                <div className="w-full aspect-square bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                  <div className="text-center">
                    <FaImage className="mx-auto text-gray-400 mb-2" size={24} />
                    <p className="text-xs text-gray-500">No image</p>
                  </div>
                </div>
              )}
              {editing && (
                <input
                  type="text"
                  value={form.product_image_url}
                  onChange={(e) =>
                    setForm({ ...form, product_image_url: e.target.value })
                  }
                  placeholder="Image URL"
                  className="w-full mt-3 px-3 py-2 text-xs border border-gray-300 rounded-md"
                />
              )}
            </div>

            {/* Metadata Card */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Metadata
              </h3>
              <div className="space-y-3 text-xs">
                <div>
                  <p className="text-gray-500">Created</p>
                  <p className="text-gray-900 mt-1">
                    {product.created_at
                      ? new Date(product.created_at).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Total Variants</p>
                  <p className="text-gray-900 mt-1">
                    {form.variants?.length || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Content */}
          <div className="col-span-9 space-y-6">
            {/* Basic Information Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                Basic Information
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Product Name"
                  value={form.product_name}
                  editing={editing}
                  onChange={(v) => setForm({ ...form, product_name: v })}
                  required
                />
                <Field
                  label="Brand Name"
                  value={form.brand_name}
                  editing={editing}
                  onChange={(v) => setForm({ ...form, brand_name: v })}
                />
                <Field
                  label="Product Type"
                  value={form.product_type}
                  editing={editing}
                  onChange={(v) => setForm({ ...form, product_type: v })}
                />
                <Field
                  label="Primary Category"
                  value={form.primary_category}
                  editing={editing}
                  onChange={(v) => setForm({ ...form, primary_category: v })}
                />
                <Field
                  label="Secondary Category"
                  value={form.secondary_category}
                  editing={editing}
                  onChange={(v) => setForm({ ...form, secondary_category: v })}
                />
                <Field
                  label="Product Link"
                  value={form.product_link}
                  editing={editing}
                  onChange={(v) => setForm({ ...form, product_link: v })}
                  type="url"
                />
              </div>
            </div>

            {/* Description Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                Description
              </h2>
              <Field
                value={form.description}
                editing={editing}
                type="textarea"
                onChange={(v) => setForm({ ...form, description: v })}
                rows={4}
              />
            </div>

            {/* Tags Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                Tags
              </h2>
              {editing ? (
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={form.tags.join(", ")}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      tags: e.target.value
                        .split(",")
                        .map((t) => t.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="Enter tags separated by commas"
                />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {form.tags.length > 0 ? (
                    form.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500 text-sm">
                      No tags assigned
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Variants Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900">
                  Variants ({form.variants?.length || 0})
                </h2>
                <button
                  onClick={() => showToast("Add variant feature coming soon")}
                  className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <FaPlus size={12} />
                  Add Variant
                </button>
              </div>

              {form.variants && form.variants.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Description
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          MRP
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Selling Price
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Created
                        </th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.variants.map((v, i) => (
                        <tr
                          key={v.variant_id || i}
                          className="border-b border-gray-100 hover:bg-gray-50"
                        >
                          <td className="py-3 px-4 text-gray-900">
                            {v.description || "Unnamed Variant"}
                          </td>
                          <td className="py-3 px-4 text-gray-900">₹{v.mrp}</td>
                          <td className="py-3 px-4 text-gray-900">
                            ₹{v.selling_price}
                          </td>
                          <td className="py-3 px-4 text-gray-600">
                            {v.created_at
                              ? new Date(v.created_at).toLocaleDateString()
                              : "-"}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button className="text-gray-400 hover:text-gray-700 p-2">
                              <FaEdit size={14} />
                            </button>
                            <button className="text-gray-400 hover:text-red-600 p-2">
                              <FaTrash size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 text-sm">No variants available</p>
                  <button
                    onClick={() => showToast("Add variant feature coming soon")}
                    className="mt-3 text-sm text-gray-700 hover:text-gray-900 font-medium"
                  >
                    Add your first variant
                  </button>
                </div>
              )}
            </div>

            {/* Remarks Card */}
            {(form.remarks || editing) && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">
                  Remarks & Notes
                </h2>
                <Field
                  value={form.remarks}
                  editing={editing}
                  type="textarea"
                  onChange={(v) => setForm({ ...form, remarks: v })}
                  rows={3}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast.message && (
        <div
          className={`fixed bottom-6 right-6 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${
            toast.type === "success" ? "bg-gray-900" : "bg-red-600"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

// Reusable Field Component
const Field = ({
  label,
  value,
  editing,
  onChange,
  type = "text",
  required = false,
  rows = 3,
}) => {
  if (!label && !editing) {
    return (
      <p className="text-sm text-gray-900 whitespace-pre-wrap">
        {value || "-"}
      </p>
    );
  }

  return (
    <div>
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      {editing ? (
        type === "textarea" ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            rows={rows}
          />
        ) : (
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          />
        )
      ) : (
        <p className="text-sm text-gray-900 whitespace-pre-wrap">
          {value || "-"}
        </p>
      )}
    </div>
  );
};

export default ProductDetails;
